import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { api, ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, Shield, ShieldCheck, ShieldOff, UserCog, Loader2, Download } from "lucide-react";

const PAGE_SIZE = 50;

// Admin Tipper tab — full user management:
//   - Server-side search by email OR username, paginated (50/page) with a
//     "Mehr laden" button so groups of 100+ tippers all load
//   - Per-user role badges and grant/revoke buttons for admin + staff
//   - Footgun guard: revoking your OWN admin role requires typing your
//     email into a confirm dialog (and is allowed only if at least one
//     OTHER admin exists, otherwise blocked entirely). adminCount comes
//     from the server (whole table), not the loaded page.

type Role = "admin" | "staff" | "user";

interface AdminUser {
  id: string;
  email: string;
  createdAt: string;
  lastLoginAt: string | null;
  profile: {
    id: string;
    username: string;
    emoji: string;
    xp: number;
    avatarUrl: string | null;
  } | null;
  roles: { role: Role }[];
}

export default function UserManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [adminCount, setAdminCount] = useState(0);
  // Bumped each time we issue a fresh (page-0) load so a late-returning
  // response from a previous search can't clobber newer state.
  const reqSeq = useRef(0);

  // Fresh load (page 0). Replaces the list. Used on mount + every search.
  const refresh = useCallback(async (q?: string) => {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const res = await api.admin.listUsers({ search: q, limit: PAGE_SIZE, offset: 0 });
      if (seq !== reqSeq.current) return; // a newer request superseded us
      setUsers((res.users ?? []) as AdminUser[]);
      setTotal(res.total ?? 0);
      setAdminCount(res.adminCount ?? 0);
    } catch (err) {
      if (seq === reqSeq.current) toast.error(err instanceof ApiError ? err.message : "Fehler beim Laden");
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, []);

  // Append the next page. Keeps current search + appends to the list.
  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const res = await api.admin.listUsers({
        search: search.trim() || undefined,
        limit: PAGE_SIZE,
        offset: users.length,
      });
      setUsers((prev) => [...prev, ...((res.users ?? []) as AdminUser[])]);
      setTotal(res.total ?? 0);
      setAdminCount(res.adminCount ?? 0);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler beim Nachladen");
    } finally {
      setLoadingMore(false);
    }
  }, [search, users.length]);

  // Debounced server-side search by email OR username.
  useEffect(() => {
    const t = setTimeout(() => refresh(search.trim() || undefined), 250);
    return () => clearTimeout(t);
  }, [search, refresh]);

  const visibleUsers = users;
  const hasMore = users.length < total;

  const hasRole = (u: AdminUser, role: Role) => u.roles.some((r) => r.role === role);

  const grant = async (u: AdminUser, role: Role) => {
    if (role === "admin") {
      const ok = window.confirm(
        `Admin-Rechte an ${u.profile?.username ?? u.email} vergeben?\n\n` +
          `Admins können:\n` +
          `• Spielergebnisse eintragen\n` +
          `• Bonus-Tipps auflösen (Punkte vergeben)\n` +
          `• Einstellungen + Schlüssel ändern\n` +
          `• Andere Admins ernennen / abberufen`,
      );
      if (!ok) return;
    }
    setBusyUserId(u.id);
    try {
      await api.admin.setRole(u.id, role);
      toast.success(`${role === "admin" ? "Admin" : "Staff"}-Rechte an ${u.profile?.username ?? u.email} vergeben`);
      // In-place update so we don't collapse already-loaded pages back to 50.
      setUsers((prev) => prev.map((x) =>
        x.id === u.id && !x.roles.some((r) => r.role === role)
          ? { ...x, roles: [...x.roles, { role }] }
          : x,
      ));
      if (role === "admin" && !hasRole(u, "admin")) setAdminCount((c) => c + 1);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler");
    } finally {
      setBusyUserId(null);
    }
  };

  const revoke = async (u: AdminUser, role: Role) => {
    // Footgun guard: removing your OWN admin role
    if (role === "admin" && u.id === me?.id) {
      if (adminCount <= 1) {
        toast.error(
          "Du bist aktuell der letzte Admin. Erst jemand anderen befördern, dann dich selbst abberufen.",
        );
        return;
      }
      const typed = window.prompt(
        `Du entziehst dir SELBST die Admin-Rechte.\n\n` +
          `Danach kommst du nicht mehr in den Admin-Bereich, bis dich ein anderer Admin wieder ernennt.\n\n` +
          `Zur Bestätigung deine E-Mail-Adresse eingeben:`,
      );
      if (typed?.trim().toLowerCase() !== me?.email.toLowerCase()) {
        toast.info("Abgebrochen.");
        return;
      }
    } else {
      const ok = window.confirm(
        `${role === "admin" ? "Admin" : "Staff"}-Rechte von ${u.profile?.username ?? u.email} entziehen?`,
      );
      if (!ok) return;
    }
    setBusyUserId(u.id);
    try {
      await api.admin.removeRole(u.id, role);
      toast.success(`${role === "admin" ? "Admin" : "Staff"}-Rechte entzogen`);
      setUsers((prev) => prev.map((x) =>
        x.id === u.id ? { ...x, roles: x.roles.filter((r) => r.role !== role) } : x,
      ));
      if (role === "admin" && hasRole(u, "admin")) setAdminCount((c) => Math.max(0, c - 1));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler");
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <h2 className="font-display font-bold text-lg flex items-center gap-2">
          <UserCog className="h-5 w-5" /> Alle Tipper
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {visibleUsers.length} von {total} · {adminCount} Admin{adminCount === 1 ? "" : "s"}
          </span>
          {/* Plain <a> so the browser handles the download via the
              Content-Disposition: attachment header. Cookie auth rides
              along on the same-origin request — no JS-fetch + blob dance
              needed. */}
          <a
            href="/api/admin/users/export"
            download
            title="Alle Tipper-E-Mail-Adressen als CSV exportieren (Excel-kompatibel)"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-border bg-card text-xs font-bold hover:bg-muted tap"
          >
            <Download className="h-3.5 w-3.5" /> CSV
          </a>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          type="search"
          placeholder="Suchen nach E-Mail oder Username …"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-10 pl-9"
        />
      </div>

      {/* Table */}
      <div className="bg-card rounded-lg shadow-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 w-12"></th>
              <th className="text-left px-3 py-2">Username</th>
              <th className="text-left px-3 py-2 hidden md:table-cell">E-Mail</th>
              <th className="text-left px-3 py-2 w-32 hidden lg:table-cell">Rollen</th>
              <th className="text-right px-3 py-2 w-16 hidden sm:table-cell">XP</th>
              <th className="text-right px-3 py-2 w-72">Aktionen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && visibleUsers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-sm text-muted-foreground">
                  Lade …
                </td>
              </tr>
            )}
            {!loading && visibleUsers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-sm text-muted-foreground">
                  Keine Tipper gefunden.
                </td>
              </tr>
            )}
            {visibleUsers.map((u) => {
              const isMe = u.id === me?.id;
              const isAdmin = hasRole(u, "admin");
              const isStaff = hasRole(u, "staff");
              const busy = busyUserId === u.id;
              return (
                <tr key={u.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-3 py-2 text-xl">{u.profile?.emoji ?? "👤"}</td>
                  <td className="px-3 py-2">
                    <div className="font-semibold flex items-center gap-1.5">
                      <span className="truncate">{u.profile?.username ?? "(kein Profil)"}</span>
                      {isMe && <span className="text-[9px] text-accent-foreground bg-accent rounded-pill px-1.5 py-0.5">DU</span>}
                    </div>
                    {/* Email + roles inline on small screens (where their own cols are hidden) */}
                    <div className="text-[10px] text-muted-foreground md:hidden truncate">{u.email}</div>
                    <div className="flex gap-1 mt-0.5 lg:hidden">
                      {isAdmin && <RoleBadge role="admin" />}
                      {isStaff && <RoleBadge role="staff" />}
                    </div>
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell text-xs text-muted-foreground truncate max-w-[240px]" title={u.email}>
                    {u.email}
                  </td>
                  <td className="px-3 py-2 hidden lg:table-cell">
                    <div className="flex gap-1">
                      {isAdmin && <RoleBadge role="admin" />}
                      {isStaff && <RoleBadge role="staff" />}
                      {!isAdmin && !isStaff && (
                        <span className="text-[10px] text-muted-foreground">user</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-muted-foreground tabular-nums hidden sm:table-cell">
                    {u.profile?.xp ?? 0}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 justify-end flex-wrap">
                      {isAdmin ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() => revoke(u, "admin")}
                          className="h-8 text-[11px] text-destructive border-destructive/30 hover:bg-destructive/10"
                          title="Admin-Rechte entziehen"
                        >
                          <ShieldOff className="h-3 w-3 mr-1" />
                          Admin entziehen
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          disabled={busy}
                          onClick={() => grant(u, "admin")}
                          className="h-8 text-[11px] bg-accent text-accent-foreground hover:bg-accent/90 font-bold"
                          title="Admin-Rechte vergeben"
                        >
                          <ShieldCheck className="h-3 w-3 mr-1" />
                          Admin geben
                        </Button>
                      )}
                      {isStaff ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() => revoke(u, "staff")}
                          className="h-8 text-[11px]"
                          title="Staff-Rechte entziehen"
                        >
                          Staff entziehen
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() => grant(u, "staff")}
                          className="h-8 text-[11px]"
                          title="Staff-Rechte vergeben (z. B. für Trikot-Ausgabe)"
                        >
                          Staff geben
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="mt-3 flex justify-center">
          <Button
            variant="outline"
            onClick={loadMore}
            disabled={loadingMore}
            className="font-bold gap-1.5"
          >
            {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Mehr laden ({total - visibleUsers.length} weitere)
          </Button>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground mt-2 px-1">
        <Shield className="h-3 w-3 inline mr-1" />
        <b>Admin</b> = volle Verwaltungsrechte (Spiele, Bonus, Einstellungen, Tipper-Rollen).{" "}
        <b>Staff</b> = darf Trikot-Codes einlösen, Activity-Feed-Posts erstellen — kein Zugriff auf Einstellungen.{" "}
        <b>user</b> = Standard-Tipper.
      </p>
    </section>
  );
}

function RoleBadge({ role }: { role: Role }) {
  const cls = {
    admin: "bg-accent text-accent-foreground",
    staff: "bg-secondary text-secondary-foreground",
    user: "bg-muted text-muted-foreground",
  }[role];
  return (
    <span className={`text-[9px] font-bold uppercase tracking-wider rounded-pill px-1.5 py-0.5 ${cls}`}>
      {role}
    </span>
  );
}
